import React, { Fragment, useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import Drawer from "components/ui-component/SideDrawer";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { DownArrow, SideArrow, UpArrow } from "icons/dashboard/downArrow";
import {
  DecLineChart,
  IncLineChart,
  NoChange,
} from "icons/dashboard/lineChart";
import RecentAndScheduledPayment from "./RecentAndScheduledPayment";
import {
  dashboardDataGet,
  leadData,
  setIsDashPreview,
  useDashboardData,
} from "store/slices/dashboardSlice";
import { useAppDispatch } from "store/hooks";
import { FilterTypes, ToastState } from "types/common";
import {
  customerDetailGet,
  useUserData,
  updateUserData,
} from "store/slices/authSlice";
import {
  amountToCurrency,
  encode,
  getBrowserTimezoneName,
  getBrowserTimezoneOffset,
} from "utils/HelperFunctions";
import PaymentMethodSection from "./PaymentMethodSection";
import SubscriptionsPreview from "components/subscriptions/SubscriptionsPreview";
import UpdatePaymentPlan from "./UpdatePaymentPlan";
import CustomModal from "components/ui-component/CustomModal";
import { cancelPaymentPlan } from "store/slices/paymentsSlice";
import { actionListGet, useActionList } from "store/slices/actionSlice";
import { UserPaymentInfoTypes } from "../../types/dashboard";
import { oldPaymentMethodsGet } from "store/slices/paymentMethodSlice";
import UpdateSusbcribePlan from "components/subscribe/UpdateSusbcribePlan";
import CancelSubscribePlan from "components/subscribe/CancelSubscribePlan";
import FailedPayment from "components/payments/FailedPayment";
import { decode } from "utils/HelperFunctions";
import { useMediaQuery } from "react-responsive";
import moment from "moment";
import { RootState } from "store/store";
import { dontCancelPlan } from "store/slices/subscriptionSlice";
import UpdatePaymentMethod from "components/subscribe/UpdatePaymentMethod";
import ScheduleUpdatePlan from "components/subscribe/ScheduleUpdatePlan";
import SubscriptionPaymentPlanSection from "./SubscriptionPaymentPlanSection";
import MessageToast from "components/ui-component/MessageToast";
import PlanPreview from "./PlanPreview";
import { onSetShowPlanDrawer } from "store/slices/commonSlice";
import UpdatePaymentPlanPreview from "./UpdatePaymentPlanPreview";
import { useTid } from "utils/testid";
import {
  getDashboardBalanceDisplay,
  getDashboardBalanceTrend,
  isDashboardBalanceAvailable,
} from "./dashboardBalance";

interface CancelProps {
  id: number | null;
  showModal: boolean;
}

export interface PlanDetails {
  id: number;
  first_name: string;
  last_name: string;
  amount: number;
  creation_date: string;
  updation_date: string;
  cc_4digit: string;
  cc_type: string;
  payment_schedule: string;
  payment_method: string;
  failed_attempts: number;
  last_payment_status: string;
  remaining_payments: number;
  total_payments: number;
  lead_owner: string;
  accounts_id: number;
  cc_fee_amount: number;
  ach_fee_amount: number;
  next_billing_date: string;
  account_number?: any;
  plan_id?: number;
  productSubscriptions?: any[];
  recurring_type?: string;
  next_payment_amount?: number | undefined;
  payment_collection_type?: string | undefined;
  sub_status?: string;
  cancel_at_period_end?: number;
  downgrade_pending?: boolean;
  canceled_at?: string | null;
  end_sub?: string | null;
  showIcon?: string;
  settlement_offer?: string | null;
  offer_type?: string | null;
  bank_name?:string
}

const DashboardPage = () => {
  const tid = useTid();
  const userData = useUserData();
  const dashboardData = useDashboardData();
  const currentBalance = dashboardData?.balance?.currentBalance;
  const lastMonthBalance = dashboardData?.balance?.lastMonthBalance;
  const currentBalanceDisplay = getDashboardBalanceDisplay(
    currentBalance,
    amountToCurrency
  );
  const currentBalanceTrend = getDashboardBalanceTrend(
    currentBalance,
    lastMonthBalance
  );
  const dispatch = useAppDispatch();
  const actionList = useActionList();
  const navigate = useNavigate();
  const isLessThan590 = useMediaQuery({ query: "(max-width: 590px)" });
  const isLessThan680 = useMediaQuery({ query: "(max-width: 680px)" });
  const isLessThan920 = useMediaQuery({ query: "(max-width: 920px)" });

  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isUpdatePlanOpen, setIsUpdatePlanOpen] = useState<boolean>(false);
  const [isPreviewId, setIsPreviewId] = useState<number>(0);
  const [updatePlanId, setUpdatePlanId] = useState<string>("");
  const [mouseHoverId, setMouseHoverId] = useState<any>(-1);
  const [filter] = useState<FilterTypes>({
    offset: 0,
    limit: 5,
  });
  const [cancelSubscribePlan, setCancelSubscribePlan] =
    useState<boolean>(false);
  const [cancelSubscribePlanId, setCancelSubscribePlanId] = useState<any>();
  const [manageUpdatePlanModal, setManageUpdatePlanModal] =
    useState<CancelProps>({
      id: null,
      showModal: false,
    });

  const [updateSubscribePlan, setUpdateSubscribePlan] =
    useState<boolean>(false);
  const [updateSubscribePlanId, setUpdateSubscribePlanId] = useState<any>();

  const [updateSubscriptionMethod, setUpdateSubscriptionMethod] =
    useState<boolean>(false);
  const [updateSubscriptionMethodId, setUpdateSubscriptionMethodId] =
    useState<any>();

  const [cancelPlan, setCancelPlan] = useState<CancelProps>({
    id: null,
    showModal: false,
  });
  const [dontCancelPlanModal, setDontCancelPlanModal] = useState<CancelProps>({
    id: null,
    showModal: false,
  });
  const [cancelBtnLoading, setCancelBtnLoading] = useState<boolean>(false);
  const [handleDrawerSize, setHandleDrawerSize] = useState<number>(0);
  const [updateData, setUpdateData] = useState<any>(null);
  const [isUpdatePreview, setIsUpdatePreview] = useState<boolean>(false);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [isApiCall, setIsApiCall] = useState<boolean>(false);
  const [finalPreviewId, setFinalPreviewId] = useState<number>(0);
  const [finalPreview, setFinalPreview] = useState<boolean>(false);
  // Which side panel is waiting on a "leave without saving?" answer. Panels are
  // dismissed by tapping the dimmed backdrop, which is easy to do by accident,
  // so anything holding half-finished input confirms first.
  type ClosableDrawer =
    | "updatePlan"
    | "updateSubscribePlan"
    | "updateSubscriptionMethod"
    | "cancelSubscribePlan"
    | "failedPayment";
  const [pendingClose, setPendingClose] = useState<ClosableDrawer | null>(null);
  const [failedPaymentDirty, setFailedPaymentDirty] = useState(false);

  const closeFailedPaymentDrawer = () => {
    setFailedPayment(false);
    setFailedPaymentId(null);
    setFailedPaymentDirty(false);
  };

  // Deferred a tick: the same click that dismisses a panel would otherwise carry
  // on to the confirmation dialog's own backdrop and close it again immediately.
  const requestDrawerClose = (drawer: ClosableDrawer) => {
    setTimeout(() => setPendingClose(drawer), 0);
  };

  const discardAndCloseDrawer = () => {
    switch (pendingClose) {
      case "updatePlan":
        setIsUpdatePlanOpen(false);
        setIsUpdatePreview(false);
        setUpdatePlanId("");
        break;
      case "updateSubscribePlan":
        setUpdateSubscribePlan(false);
        setUpdateSubscribePlanId(null);
        setHandleDrawerSize(0);
        break;
      case "updateSubscriptionMethod":
        setUpdateSubscriptionMethod(false);
        setUpdateSubscriptionMethodId(null);
        break;
      case "cancelSubscribePlan":
        setCancelSubscribePlan(false);
        setCancelSubscribePlanId(null);
        break;
      case "failedPayment":
        closeFailedPaymentDrawer();
        break;
    }
    setPendingClose(null);
  };

  const subscriptionPaymentPlan = useMemo(() => {
    return dashboardData?.subscriptionPaymentPlan;
  }, [dashboardData]);

  const [msg, setMsg] = useState<ToastState>({
    variant: "",
    content: null,
  });
  const [isDontCancelLoading, setIsDontCancelLoading] =
    useState<boolean>(false);
  const [data, setData] = useState<UserPaymentInfoTypes>([]);
  const [failedPayment, setFailedPayment] = useState<boolean>(false);
  const [failedPaymentId, setFailedPaymentId] = useState<string | null>(null);
  const [errrorMessage, setErrorMessage] = useState<any>("");

  const { showPlanDrawer } = useSelector((state: RootState) => state.common);

  useEffect(() => {
    // Always hand the flag back when this preview goes away or the screen
    // grows past the mobile breakpoint. Without that, navigating away with
    // the preview open left it stuck on, and the layout kept the mobile
    // header and footer hidden on every page.
    if (!isLessThan590) {
      dispatch(setIsDashPreview?.(false));
      return;
    }
    dispatch(setIsDashPreview?.(isPreview));
    return () => {
      dispatch(setIsDashPreview?.(false));
    };
  }, [isPreview, isLessThan590]);

  const onGetDashboardData = async () => {
    try {
      let payload = {
        ...filter,
        accounts_id: userData?.data?.accountData?.id,
      };

      await dispatch(dashboardDataGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      console.error("get payment history err: -->", (error as Error).message);
    }
  };

  const onGetCustomerDetails = async () => {
    try {
      let params: any = {
        accounts_id: userData?.data?.accountData?.id,
      };
      let res: any = await dispatch(customerDetailGet(params));
      let data = {
        ...userData?.data?.accountData,
        ...res?.payload?.body?.data,
        balance: res?.payload?.body?.data?.balance,
      };
      await dispatch(updateUserData(data));
    } catch (error: any) {
      console.log("onGetCustomerDetails error =====", error);
    }
  };

  const onGetLeadData = async () => {
    let lead_payload: any = {
      accounts_id: userData.data.accountData.id,
      cluster_id: userData.data.accountData.clusterid,
      accountid: userData.data.accountData.accountid,
    };
    try {
      await dispatch(leadData(lead_payload)).unwrap();
    } catch (error: any) {
      console.log("onGetLeadData error", error);
    }
  };

  const onGetActionList = async () => {
    try {
      let payload: any = {
        // Deliberately more than fits on a typical screen: the Action Center
        // trims the list to whatever the available height allows, so it needs a
        // pool to draw from on taller displays.
        limit: 12,
        skip: 0,
        accounts_id: userData?.data?.accountData?.id,
        status: [],
      };
      await dispatch(actionListGet(payload)).unwrap();
    } catch (error: any) {
      console.log("get payment history err: -->", error?.message);
    }
  };

  const onOldCardDataGet = async () => {
    try {
      let payload = {
        accountid: userData?.data?.accountData?.accountid,
        accounts_id: userData?.data?.accountData?.id,
        cluster_id: clusterId,
      };
      await dispatch(oldPaymentMethodsGet(payload)).unwrap();
    } catch (error: any) {
      console.log("old card data get erro:-->", error?.message);
    }
  };

  useEffect(() => {
    if (userData) {
      onGetLeadData();
      onGetActionList();
      onOldCardDataGet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  useEffect(() => {
    onGetDashboardData();
    onGetCustomerDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showPlanDrawer) {
      dispatch(onSetShowPlanDrawer(null));
      navigate(`/${cluster_id}/subscriptions-plans`);
    }
  }, [showPlanDrawer]);

  useEffect(() => {
    setData([
      {
        title: "Current Balance",
        // A settled account has a real balance of 0 — only a missing value
        // means "no balance to show", so the checks must not treat 0 as absent.
        value: isLoading ? (
          <Skeleton height={33} width={180} />
        ) : (
          currentBalanceDisplay.value
        ),
        text: isLoading ? (
          <Skeleton height={20} width={129} />
        ) : (
          currentBalanceDisplay.text
        ),
        chart: true,
        clicked: false,
        // Spans the full width of the small-screen grid so the trend chart keeps
        // room to sit beside the figure.
        className:
          "col-span-2 lg:col-auto flex lg:gap-[20px] lg:w-fit w-full lg:justify-start justify-between",
      },
      {
        title: "Last Payment",
        value: isLoading ? (
          <Skeleton height={33} width={126} />
        ) : dashboardData?.lastPayment?.amount ? (
          amountToCurrency(dashboardData?.lastPayment?.amount || 0)
        ) : (
          "--"
        ),
        text: isLoading ? (
          <Skeleton height={20} width={129} />
        ) : dashboardData?.lastPayment?.insert_date &&
          dashboardData?.lastPayment?.amount ? (
          moment(dashboardData?.lastPayment?.insert_date).format("MMM D, YYYY")
        ) : (
          "N/A"
        ),
        chart: false,
        clicked: false,
        className: "",
      },
      {
        title: "Next Payment",
        value: isLoading ? (
          <Skeleton height={33} width={126} />
        ) : dashboardData?.nextPayment?.amount ? (
          amountToCurrency(
            dashboardData?.nextPayment?.amount +
              (dashboardData?.nextPayment?.cf_amount ?? 0) || 0
          )
        ) : (
          "--"
        ),
        text: isLoading ? (
          <Skeleton height={20} width={126} />
        ) : dashboardData?.nextPayment?.date &&
          dashboardData?.nextPayment?.amount ? (
          moment(dashboardData?.nextPayment?.date).format("MMM D, YYYY")
        ) : (
          "N/A"
        ),
        chart: false,
        clicked: false,
        className: "",
      },
      {
        title: "Failed Payments",
        value: isLoading ? (
          <Skeleton height={33} width={153} />
        ) : dashboardData?.totalFailedPayments ? (
          dashboardData?.totalFailedPayments
        ) : (
          "--"
        ),
        text: isLoading ? (
          <Skeleton height={33} width={113} />
        ) : dashboardData?.totalFailedPayments ? (
          "View details"
        ) : (
          "N/A"
        ),
        chart: false,
        clicked: true,
        clickedFunction: () =>
          navigate(`/${cluster_id}/payments`, { state: { status: "failed" } }),
        className: "",
      },
      {
        title: "Active Plans",
        value: isLoading ? (
          <Skeleton height={33} width={113} />
        ) : dashboardData?.activePaymentPlan ? (
          dashboardData?.activePaymentPlan
        ) : (
          "--"
        ),
        text: isLoading ? (
          <Skeleton height={33} width={113} />
        ) : dashboardData?.activePaymentPlan ? (
          "Manage plans"
        ) : (
          "N/A"
        ),
        chart: false,
        clicked: true,
        clickedFunction: () =>
          navigate(`/${cluster_id}/subscriptions-plans`, {
            state: { status: "active" },
          }),
        className: "",
      },
    ]);
    // dashboardData must be here: these tiles read every figure from it, and
    // actions like cancelling or updating a plan refetch it. Watching only the
    // initial loading flag left the tiles showing pre-action values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, dashboardData]);

  const handleCancelPaymentPlan = async () => {
    setCancelBtnLoading(true);
    try {
      const payload = {
        id: cancelPlan.id,
        email: [],
        reason: "",
        cluster_id: "",
        lead_id: "",
        accounts_id: userData?.data?.accountData?.id,
        agent: "",
        timezone: getBrowserTimezoneOffset(),
        customer_timezone: getBrowserTimezoneName(),
      };
      let res: any = await dispatch(cancelPaymentPlan(payload)).unwrap();
      setMsg({
        content: res.body.success
          ? "Plan canceled successfully"
          : "Plan cancellation failed",
        variant: res.body.success ? "success" : "error",
      });

      setCancelBtnLoading(false);
      onGetDashboardData();
      setCancelPlan({ id: null, showModal: false });
    } catch (error) {
      console.log("handleCancelPaymentPlan error ===========", error);
      setMsg({
        content: "Plan cancellation failed",
        variant: "error",
      });
      setCancelBtnLoading(false);
    }
  };

  const handleDontCancelPaymentPlan = async () => {
    if (!dontCancelPlanModal.id) return;
    setIsDontCancelLoading(true);

    try {
      let payload = {
        subscription_id: dontCancelPlanModal.id,
      };

      let res: any = await dispatch(dontCancelPlan(payload)).unwrap();
      if (res?.body?.success) {
        setMsg({
          content: "Your subscription will remain active.",
          variant: "success",
        });
      }
    } catch (error) {
      console.log("handleDontCancelPaymentPlan error ======", error);
    } finally {
      setIsDontCancelLoading(false);
      onGetDashboardData();
      setDontCancelPlanModal({ id: null, showModal: false });
    }
  };

  // To open the drawer for failed payment
  useEffect(() => {
    if (searchParams.has("drawer") && searchParams.has("failedOrderId")) {
      setFailedPayment(true);
      setFailedPaymentId(searchParams.get("failedOrderId"));
    }
  }, []);

  const calculatePercentageDifference = (
    current: number = 0,
    lastMonth: number = 0
  ): number => {
    let percent = 0;
    if (lastMonth === 0) {
      percent = current === 0 ? 0 : 100;
    } else if (current === lastMonth) {
      percent = 0;
    } else {
      let denominator = lastMonth < 0 ? lastMonth * -1 : lastMonth;
      percent = ((current - lastMonth) / denominator) * 100;
    }

    return percent;
  };

  useEffect(() => {
    if (
      searchParams.has("subsDrawer") &&
      searchParams.get("subsDrawer") === "true"
    ) {
      if (searchParams.get("isUpdateSubscriptionPaymentMethod") === "true") {
        setUpdateSubscriptionMethod(true);
        let id = searchParams.get("subscriptionId");
        setUpdateSubscriptionMethodId(
          encode(`${id}_${clusterId}_${new Date().getTime()}`, "sub_")
        );
      } else {
        setUpdateSubscribePlan(true);
        let id = searchParams.get("subscriptionId");
        setUpdateSubscribePlanId(
          encode(`${id}_${clusterId}_${new Date().getTime()}`, "sub_")
        );
      }
    }

    if (
      searchParams.has("plansDrawer") &&
      searchParams.get("plansDrawer") === "true" &&
      searchParams.has("planId")
    ) {
      setIsUpdatePlanOpen(true);
      let id = searchParams.get("planId") ?? "";
      setUpdatePlanId(id);
    }
  }, []);

  return (
    <>
      <div
        className="lg:pl-10 2xl:pr-0 px-5 lg:pt-[30px] h-full"
        data-testid={tid("dashboard-page")}
      >
        {/* 1182 : 374 is the column split from the design (with the 30px gutter). */}
        <div className="h-fit w-full 2xl:grid 2xl:grid-cols-[1182fr_374fr] gap-[30px]">
          {/* left part */}
          <div className="w-full h-fit">
            <div
              className="grid grid-cols-2 gap-x-6 gap-y-10 w-full lg:flex lg:gap-6 lg:justify-between lg:flex-wrap"
              data-testid={tid("dashboard-summary")}
              aria-label="Dashboard summary"
            >
              {data?.map((list: any, index: number) => {
                const summaryId = String(list.title || `summary-${index}`)
                  .trim()
                  .replace(/[^a-zA-Z0-9]+/g, "-")
                  .replace(/^-+|-+$/g, "")
                  .toLowerCase();
                return (
                  <div
                    className={list.className}
                    key={list.title}
                    data-testid={tid(`summary-${summaryId}`)}
                  >
                    <div
                      className={list.clicked ? "cursor-pointer" : undefined}
                      role={list.clicked ? "button" : undefined}
                      tabIndex={list.clicked ? 0 : undefined}
                      aria-label={list.clicked ? list.title : undefined}
                      data-testid={
                        list.clicked
                          ? tid(`summary-${summaryId}-action`)
                          : undefined
                      }
                      onClick={() => {
                        if (list.clicked) {
                          list.clickedFunction();
                        }
                      }}
                      onMouseEnter={() => setMouseHoverId(index)}
                      onMouseLeave={() => setMouseHoverId(-1)}
                    >
                      <p className="text-txtParagraph lg:text-xl text-lg font-medium">
                        {list.title}
                      </p>
                      <p className="lg:text-[32px] text-3xl font-medium text-txtPrimary lg:mt-3 mt-2">
                        {list.value || "--"}
                      </p>
                      <p
                        className={`lg:text-base text-sm font-normal  ${
                          mouseHoverId === index && list.clicked
                            ? "text-blue underline"
                            : "text-txtSecondary"
                        }`}
                      >
                        {list.text || "N/A"}
                      </p>
                    </div>
                    {list.chart &&
                      (isLoading ? (
                        <div className="flex flex-col items-center gap-4">
                          <Skeleton width={83} height={52} />
                          <Skeleton width={53} height={25} />
                        </div>
                      ) : (
                        <>
                          {currentBalanceDisplay.isAvailable &&
                          isDashboardBalanceAvailable(lastMonthBalance) ? (
                            <div className="flex flex-col justify-between items-center">
                              <div
                                data-testid={tid("current-balance-trend")}
                                className={`${
                                  currentBalanceTrend === "unchanged"
                                    ? "pt-9"
                                    : "pt-4"
                                } flex items-center`}
                              >
                                {currentBalanceTrend === "unchanged" ? (
                                  <NoChange />
                                ) : currentBalanceTrend === "decreased" ? (
                                  <DecLineChart />
                                ) : (
                                  <IncLineChart />
                                )}
                              </div>
                              <div className="flex justify-end ">
                                <div
                                  className={`px-[6px] rounded-[16px] flex ${
                                    currentBalanceTrend === "unchanged"
                                      ? "bg-[#FDB6000D] text-yellow"
                                      : currentBalanceTrend === "decreased"
                                        ? "bg-[#FF00000D] text-red"
                                        : "bg-[#23B3391A] text-green"
                                  } py-[2px] justify-center gap-1 items-center`}
                                  data-testid={tid(
                                    "current-balance-trend-percent"
                                  )}
                                >
                                  {currentBalanceTrend === "unchanged" ? (
                                    <SideArrow />
                                  ) : currentBalanceTrend === "decreased" ? (
                                    <DownArrow />
                                  ) : (
                                    <UpArrow />
                                  )}
                                  <div className="lg:text-sm text-xs  font-medium">
                                    {/* The arrow already carries the direction,
                                        so the sign would read twice over. */}
                                    {Math.abs(
                                      Math.round(
                                        calculatePercentageDifference(
                                          currentBalance,
                                          lastMonthBalance
                                        ) || 0
                                      )
                                    )}
                                    %
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <></>
                          )}
                        </>
                      ))}
                  </div>
                );
              })}
            </div>

            <SubscriptionPaymentPlanSection
              subscriptionPaymentPlan={subscriptionPaymentPlan}
              isLoading={isLoading}
              onPreviewClick={(id: number) => {
                setIsPreview(true);
                setIsPreviewId(id);
              }}
              onUpdatePlanClick={(planDetails: PlanDetails) => {
                if (planDetails?.recurring_type === "subscription") {
                  setUpdateSubscribePlan(true);
                  setUpdateSubscribePlanId(
                    encode(
                      `${planDetails?.id}_${clusterId}_${new Date().getTime()}`,
                      "sub_"
                    )
                  );
                } else {
                  setIsUpdatePlanOpen(true);
                  setUpdatePlanId(
                    encode(
                      `${planDetails?.id}_${clusterId}_${new Date().getTime()}`,
                      "plan_"
                    )
                  );
                }
              }}
              onUpdatePaymentMethodClick={(planDetails: PlanDetails) => {
                let slug = encode(
                  `${planDetails?.id}_${clusterId}_${new Date().getTime()}`,
                  "sub_"
                );
                setUpdateSubscriptionMethodId(slug);
                setUpdateSubscriptionMethod(true);
              }}
              onScheduleUpdateClick={(id: number) => {
                setManageUpdatePlanModal({
                  showModal: true,
                  id: id,
                });
              }}
              onCancelPlanClick={(planDetails: PlanDetails) => {
                if (planDetails?.recurring_type === "subscription") {
                  setCancelSubscribePlan(true);
                  setCancelSubscribePlanId(planDetails?.id);
                } else {
                  setCancelPlan({
                    id: planDetails.id,
                    showModal: true,
                  });
                }
              }}
              onDontCancelClick={(id: number) => {
                setDontCancelPlanModal({
                  id: id,
                  showModal: true,
                });
              }}
              userData={userData}
            />

            <div className="2xl:hidden flex w-full mt-[30px] items-center lg:flex-row lg:gap-2 xl:flex-nowrap flex-wrap flex-col">
              <PaymentMethodSection
                actionList={actionList?.actionListData}
                isLoading={isLoading}
                onOldCardDataGet={onOldCardDataGet}
                onGetDashboardData={onGetDashboardData}
              />
            </div>

            <RecentAndScheduledPayment
              isLoading={isLoading}
              setFailedPayment={setFailedPayment}
              setFailedPaymentId={setFailedPaymentId}
            />
          </div>
          {/* right part */}
          <div className="w-full 2xl:block hidden">
            <PaymentMethodSection
              actionList={actionList?.actionListData}
              isLoading={isLoading}
              onOldCardDataGet={onOldCardDataGet}
              onGetDashboardData={onGetDashboardData}
            />
          </div>
        </div>
      </div>

      <Drawer
        enableOverlay={true}
        direction="right"
        open={isPreview}
        size={isLessThan590 ? `100vw` : "590px"}
        onClose={() => {
          setIsPreview(false);
          setIsPreviewId(0);
        }}
        overlayColor="#000"
        lockBackgroundScroll={true}
        zIndex={999}
      >
        <SubscriptionsPreview
          id={isPreviewId}
          setId={setIsPreviewId}
          show={isPreview}
          setShow={setIsPreview}
          setHandleDrawerSize={setHandleDrawerSize}
        />
      </Drawer>

      {/* Read-only preview: closes straight away, there is nothing to lose. */}
      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={isUpdatePreview}
        size={isLessThan590 ? `100vw` : "590px"}
        onClose={() => {
          setIsUpdatePreview(false);
          setHandleDrawerSize(0);
        }}
        overlayColor="#000"
      >
        <UpdatePaymentPlanPreview
          show={isUpdatePreview}
          setShow={setIsUpdatePreview}
          updateData={updateData}
          setHandleDrawerSize={setHandleDrawerSize}
        />
      </Drawer>

      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={updateSubscribePlan}
        size={`${handleDrawerSize ? "0" : isLessThan680 ? `100vw` : "680px"}`}
        onClose={() => requestDrawerClose("updateSubscribePlan")}
        overlayColor="#000"
      >
        <UpdateSusbcribePlan
          id={updateSubscribePlanId}
          show={updateSubscribePlan}
          setShow={setUpdateSubscribePlan}
          setId={setUpdateSubscribePlanId}
          fetchDetails={() => onGetDashboardData()}
          setHandleDrawerSize={setHandleDrawerSize}
          setIsPreview={setIsPreview}
          setIsPreviewId={setIsPreviewId}
          errrorMessage={errrorMessage}
          setErrorMessage={setErrorMessage}
        />
      </Drawer>

      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={updateSubscriptionMethod}
        size={`${isLessThan680 ? `100vw` : "680px"}`}
        onClose={() => requestDrawerClose("updateSubscriptionMethod")}
        overlayColor="#000"
      >
        <UpdatePaymentMethod
          id={updateSubscriptionMethodId}
          show={updateSubscriptionMethod}
          setShow={setUpdateSubscriptionMethod}
          setId={setUpdateSubscriptionMethodId}
          fetchDetails={() => onGetDashboardData()}
          setHandleDrawerSize={setHandleDrawerSize}
          setIsPreview={setIsPreview}
          setIsPreviewId={setIsPreviewId}
          // errrorMessage={errrorMessage}
          // setErrorMessage={setErrorMessage}
        />
      </Drawer>

      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={cancelSubscribePlan}
        size={`${handleDrawerSize ? "0" : isLessThan680 ? `100vw` : "680px"}`}
        onClose={() => requestDrawerClose("cancelSubscribePlan")}
        overlayColor="#000"
      >
        <CancelSubscribePlan
          id={cancelSubscribePlanId}
          show={cancelSubscribePlan}
          setShow={setCancelSubscribePlan}
          setId={setCancelSubscribePlanId}
          fetchDetails={() => onGetDashboardData()}
          setHandleDrawerSize={setHandleDrawerSize}
          setIsPreview={setIsPreview}
          setIsPreviewId={setIsPreviewId}
        />
      </Drawer>
      {/* No pointer-events-none here: the dimmed backdrop has to absorb clicks,
          otherwise the page behind stays live and a stray click navigates away
          mid-edit. Closing via the backdrop asks for confirmation first; the
          drawer's own X still closes directly. */}
      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={isUpdatePlanOpen}
        size={`${handleDrawerSize ? "0" : isLessThan920 ? `100vw` : "920px"}`}
        onClose={() => requestDrawerClose("updatePlan")}
        overlayColor="#000"
      >
        <UpdatePaymentPlan
          id={updatePlanId}
          show={isUpdatePlanOpen}
          setShow={setIsUpdatePlanOpen}
          setUpdatePlanId={(value?: string) => {
            setUpdatePlanId(value || "");
          }}
          setSelectedMethod={setSelectedMethod}
          fetchDetails={() => {
            onGetDashboardData();
            onGetCustomerDetails();
          }}
          setIsPreview={setIsUpdatePreview}
          setHandleDrawerSize={setHandleDrawerSize}
          setUpdateData={setUpdateData}
          isApiCall={isApiCall}
          setIsApiCall={setIsApiCall}
          setFinalPreview={setFinalPreview}
          setFinalPreviewId={(value?: string) => {
            setFinalPreviewId(parseInt(value || "0"));
          }}
          isFinalPreview={finalPreview}
        />
      </Drawer>

      {/* The width check that used to guard this meant the panel could not be
          dismissed at all on a phone. */}
      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={failedPayment}
        size={`${isLessThan680 ? `100vw` : "680px"}`}
        // Only confirm when there is something to lose: dismissing a panel the
        // customer has not typed into yet should just close it.
        onClose={() =>
          failedPaymentDirty
            ? requestDrawerClose("failedPayment")
            : closeFailedPaymentDrawer()
        }
        overlayColor="#000"
      >
        <FailedPayment
          id={failedPaymentId}
          show={failedPayment}
          setShow={setFailedPayment}
          setId={setFailedPaymentId}
          onDirtyChange={setFailedPaymentDirty}
          fetchDetails={() => {
            onGetDashboardData();
            onGetCustomerDetails();
          }}
        />
      </Drawer>

      <CustomModal
        signModal={false}
        isLoading={cancelBtnLoading}
        isOpen={cancelPlan?.showModal}
        title="Cancel Plan"
        data-testid={tid("cancel-plan-modal")}
        aria-label="Cancel plan"
        doneText="Cancel Plan"
        className="max-w-[600px] w-full h-fit rounded-[10px]"
        cancleText="Not Now"
        onDoneClick={() => {
          handleCancelPaymentPlan();
        }}
        onCloseClick={() => {
          setCancelPlan({
            id: null,
            showModal: false,
          });
        }}
        children={
          <Fragment>
            <div className="pt-5 lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]">
              Are you sure you want to proceed? Cancellation is permanent and
              cannot be undone.
            </div>
          </Fragment>
        }
      />

      <CustomModal
        signModal={false}
        isOpen={pendingClose !== null}
        title="Leave without saving?"
        data-testid={tid("leave-drawer-modal")}
        aria-label="Leave without saving"
        doneText="Leave"
        className="max-w-[600px] w-full h-fit rounded-[10px]"
        cancleText="Keep editing"
        onDoneClick={discardAndCloseDrawer}
        onCloseClick={() => {
          setPendingClose(null);
        }}
        children={
          <div className="pt-5 lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]">
            Anything you have entered here has not been saved yet. If you leave
            now, it will be lost.
          </div>
        }
      />

      <CustomModal
        signModal={true}
        isOpen={manageUpdatePlanModal?.showModal}
        title="Alert"
        data-testid={tid("schedule-update-plan-modal")}
        aria-label="Schedule update plan"
        doneText="OK"
        className="max-w-[600px] w-full px-5 h-fit rounded-[10px]"
        cancleText="Cancel"
        onDoneClick={() => {
          // handleDontCancelPaymentPlan();
        }}
        onCloseClick={() => {
          setManageUpdatePlanModal({
            id: null,
            showModal: false,
          });
        }}
      >
        <ScheduleUpdatePlan
          id={manageUpdatePlanModal?.id || null}
          setShowModal={setManageUpdatePlanModal}
          show={manageUpdatePlanModal?.showModal}
          setMsg={setMsg}
          fetchDetails={() => {
            onGetDashboardData();
          }}
        />
      </CustomModal>

      <CustomModal
        signModal={false}
        isLoading={isDontCancelLoading}
        isOpen={dontCancelPlanModal?.showModal}
        title="Alert"
        data-testid={tid("keep-subscription-modal")}
        aria-label="Keep subscription active"
        doneText="OK"
        className="max-w-[600px] w-full px-5 h-fit rounded-[10px]"
        cancleText="Cancel"
        onDoneClick={() => {
          handleDontCancelPaymentPlan();
        }}
        onCloseClick={() => {
          setDontCancelPlanModal({
            id: null,
            showModal: false,
          });
        }}
        children={
          <div className="pt-5 w-full  lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]">
            This will undo the scheduled cancelation and keep your subscription
            active beyond the current billing cycle.
            <br />
            <br />
            Are you sure you want to continue the subscription?
          </div>
        }
      />

      <MessageToast
        variant={msg?.variant}
        content={msg?.content}
        isProgressBar
        width="w-full max-w-[810px] px-5"
        className=" !absolute !right-1/2 !translate-x-1/2"
        onCloseClick={() => {
          setMsg({
            ...msg,
            variant: "",
            content: null,
          });
        }}
      />
    </>
  );
};

export default DashboardPage;
